import dayjs from "dayjs";
import React from "react";

import {
  BookingQuestionId,
  ExperienceBookingDetails,
  ExperienceReservation,
  PaymentBreakdown,
  PaymentLineItemEnum,
  PaymentLineItemRewards,
  PaymentLineItemTravelWalletCredit,
  PaymentLineItemUserCard,
} from "redmond";
import { ILabel, IValues } from "../FlightCard/helpers";
import {
  formatFiatValue,
  formatRewardsValue,
} from "../../../../../../utils/helpers";

// Note: "Experience" has renamed to "Activity"

export const CONFIRMATION = "Confirmation:";
export const EXPERIENCE_HEADER_TEXT = "Activity";
export const VIEW_DETAILS = "View details";
export const VIEW_LESS = "View less";
export const DATE_FORMAT = "ddd, MMM D";
export const CANCELED = "Canceled";
export const CANCELED_RES = "This reservation has been canceled.";
export const DETAILS = "Details";
export const TRAVELER = "Traveler";
export const TRAVELERS = (travelerNames: string[]) =>
  `Travelers (${travelerNames.length})`;

export const getHeaderText = (travelDate?: string) =>
  travelDate ? (
    <>
      <strong>{EXPERIENCE_HEADER_TEXT}</strong>{" "}
      <span className="separator">|</span>{" "}
      {dayjs(travelDate).format(DATE_FORMAT)}
    </>
  ) : (
    <strong>{EXPERIENCE_HEADER_TEXT}</strong>
  );

export const getExperienceInfoDetails = ({
  reservation,
}: ExperienceReservation) => {
  const { customerReservationId, experienceDetails, experienceSelection } =
    reservation;
  const { name, supplierInfo, destination } = experienceDetails || {};
  const { travelDate, providerExperienceStartTime } = experienceSelection;

  return {
    title: destination,
    confirmationLabel: CONFIRMATION,
    confirmationCode: customerReservationId,
    startLabel: "",
    endLabel: "",
    startDate: dayjs(travelDate).format(DATE_FORMAT),
    endDate: providerExperienceStartTime,
    subtitle: name || "",
    supplierName: supplierInfo?.name || "",
    phone: supplierInfo?.phone || "",
    email: supplierInfo?.email || "",
  };
};

export const CANCEL_EXPERIENCE = "Cancel activity";
export const RESEND_CONFIRMATION = "Resend confirmation";
export const SHOW_VOUCHER = "Show voucher";

export const getLabelsFromPaymentBreakdown = (breakdown: PaymentBreakdown) => {
  const labels: ILabel = {
    cardLabel: null,
    rewardLabel: null,
  };
  breakdown.payments.forEach((payment) => {
    switch (payment.PaymentLineItem) {
      case PaymentLineItemEnum.UserCard:
        labels.cardLabel = (
          payment as PaymentLineItemUserCard
        ).cardNumberDisplay;
        break;
      case PaymentLineItemEnum.Rewards:
        labels.rewardLabel = (
          payment as PaymentLineItemRewards
        ).accountDisplayName;
        break;
      case PaymentLineItemEnum.TravelWallet:
        labels.rewardLabel = "Travel credit applied";
        break;
      default:
    }
  });
  return labels;
};

export const getValues = (breakdown: PaymentBreakdown) => {
  const values: IValues = {
    cardValue: null,
    rewardValue: null,
  };
  breakdown.payments.forEach((payment) => {
    switch (payment.PaymentLineItem) {
      case PaymentLineItemEnum.UserCard: {
        const cardAmt = (payment as PaymentLineItemUserCard).amount;
        values.cardValue = `${cardAmt.currency} $${formatFiatValue(
          cardAmt.amount
        )}`;
        break;
      }
      case PaymentLineItemEnum.Rewards: {
        const rewardAmt = payment as PaymentLineItemRewards;
        values.rewardValue = `${formatRewardsValue(
          rewardAmt.amount.rewardsPrice.value
        )} ${rewardAmt.amount.rewardsPrice.currency}`;
        break;
      }
      case PaymentLineItemEnum.TravelWallet: {
        const walletAmt = payment as PaymentLineItemTravelWalletCredit;
        values.rewardValue = `-$${formatFiatValue(walletAmt.amount.amount)}`;
        break;
      }
      default:
    }
  });
  return values;
};

// Experience Card Content
export const STARTING_POINT = "Starting point";
export const END_POINT = "End point";
export const STARTING_AND_END_POINT = "Starting & end point";
export const END_SAME_AS_START = "This activity ends at the starting point.";
export const VIEW_ITINERARY = "View itinerary";
export const CANCELLATION_POLICY = "Cancellation Policy";
export const VIEW_POLICY_DETAILS = "View policy details";

export const getTravelerNames = (
  bookingDetails?: ExperienceBookingDetails
): string[] => {
  if (!bookingDetails) return [""];

  const { bookerInfo, bookingQuestionAnswers } = bookingDetails;

  const firstNames = bookingQuestionAnswers.filter(
    (answer) => answer.bookingQuestionId === BookingQuestionId.FullNamesFirst
  );
  const lastNames = bookingQuestionAnswers.filter(
    (answer) => answer.bookingQuestionId === BookingQuestionId.FullNamesLast
  );

  const travelerNames = [];

  if (!!firstNames.length && !!lastNames.length) {
    firstNames.forEach(({ answer }, idx) => {
      const lastName = lastNames[idx].answer;
      travelerNames.push(`${answer} ${lastName}`);
    });
  } else {
    travelerNames.push(`${bookerInfo.firstName} ${bookerInfo.lastName}`);
  }

  return travelerNames;
};
